import { NgZone } from '@angular/core';
import mapboxgl from 'mapbox-gl';
import { IReporteBase } from 'modelos/src';
import { VehicleIconGeneratorService } from './services/vehicle-icon-generator.service';
import { OsrmService } from '../servicios/osrm.service';

/**
 * Configuración del HTML marker para vehículo seleccionado
 */
export interface HTMLVehicleMarkerConfig {
  scale: number; // Escala del ícono (0.5 a 2)
  categoria: 'tipo' | 'funcion'; // Categoría del marker
  showText: boolean; // Mostrar texto identificación
  pitchAlignment: 'viewport' | 'map'; // Alineación del pitch
  animationDuration: number; // Duración de animación en ms (default: 7000)
  enableAnimation: boolean; // Habilitar animación de movimiento (default: true)
  mostrarFoto?: 'no' | 'centrado' | 'completo'; // Modo de foto del activo
}

/**
 * Servicios externos requeridos por HTMLVehicleMarker
 */
export interface HTMLVehicleMarkerServices {
  helper: any; // MapaVehiculosHelperService
  vehicleIconGenerator: VehicleIconGeneratorService;
  getVehiculoEstilos: (
    reporte: IReporteBase<'Tracker 4G'>,
    categoria: 'tipo' | 'funcion',
  ) => Promise<any>;
  ngZone: NgZone;
  osrm?: OsrmService; // OsrmService (opcional para compatibilidad)
  onRoutePathUpdate?: (pathCoordinates: [number, number][]) => void; // Callback para actualizar ruta en el mapa
}

/**
 * Encapsula toda la lógica de un HTML marker para vehículo seleccionado en Mapbox
 * Maneja creación, actualización, animación y destrucción de forma centralizada
 */
export class HTMLVehicleMarker {
  // Referencias a Mapbox markers
  private iconMarker?: mapboxgl.Marker;
  private textMarker?: mapboxgl.Marker;

  // Estado actual
  private heading: number = 0;
  private velocity: number = 0;
  private ignition: boolean | undefined;
  private currentLng: number = 0;
  private currentLat: number = 0;
  private lastUpdateTimestamp: number = 0;

  // Animación del marker
  private animation?: {
    frame: number;
    originLng: number;
    originLat: number;
    targetLng: number;
    targetLat: number;
    startTime: number;
    duration: number;
  };

  // Configuración
  private config: HTMLVehicleMarkerConfig;

  // Listeners
  private rotateHandler?: () => void;
  private interactionHandlers: { event: string; handler: () => void }[] = [];

  // Estado de inicialización
  private initialized: boolean = false;
  private destroyed: boolean = false;

  // ID del vehículo que representa este marker
  public readonly vehicleId: string;
  // Categoría del marker (para detectar cambios tipo/función)
  public readonly categoria: 'tipo' | 'funcion';
  // Modo foto activo (para detectar cambios)
  public readonly mostrarFoto: 'no' | 'centrado' | 'completo';

  constructor(
    private map: mapboxgl.Map,
    private reporte: IReporteBase<'Tracker 4G'>,
    config: HTMLVehicleMarkerConfig,
    private services: HTMLVehicleMarkerServices,
  ) {
    this.config = { ...config };
    this.vehicleId = reporte.activo?._id || '';
    this.categoria = config.categoria;
    this.mostrarFoto = config.mostrarFoto ?? 'no';
  }

  /**
   * Inicializa el marker (async porque requiere generar imágenes)
   * @param initialCoords Coordenadas iniciales [lng, lat]. Si se proveen, se usan estas
   *   (permite usar snappedGeojson desde el exterior). Si no, usa valores.geojson.
   */
  public async initialize(initialCoords?: [number, number]): Promise<void> {
    if (this.initialized || this.destroyed) return;

    // Obtener valores iniciales del reporte
    const valores = this.services.helper.getValores(this.reporte);

    // Usar coordenadas provistas (snapped u otras) o caer al geojson bruto
    const coords = initialCoords || valores.geojson?.coordinates;
    if (!coords) {
      throw new Error('Reporte sin coordenadas válidas');
    }

    const [lng, lat] = coords;
    this.currentLng = lng;
    this.currentLat = lat;
    this.heading = valores.orientacion || 0;
    this.velocity = valores.velocidad || 0;
    this.ignition = valores.ignicion;

    // Crear elementos del marker
    await this.createMarkerElements();

    // Setup listeners del mapa
    this.setupMapListeners();

    this.initialized = true;
  }

  /**
   * Actualiza la posición del marker (con animación opcional)
   */
  public async updatePosition(
    lng: number,
    lat: number,
    heading: number,
    animated: boolean = true,
  ): Promise<void> {
    if (!this.initialized || this.destroyed) return;

    // Actualizar heading inmediatamente (se sobrescribirá durante animación si usa OSRM)
    this.heading = heading;
    this.updateRotation();

    // Animar solo si está habilitado globalmente (enableAnimation) y localmente (animated)
    if (
      animated &&
      this.config.enableAnimation &&
      this.config.animationDuration > 0
    ) {
      // Calcular duración dinámica basada en tiempo transcurrido y distancia
      const duration = this.calculateDynamicDuration(lng, lat);

      // Calcular distancia para decidir si usar OSRM
      const distance = this.calculateDistance(
        this.currentLng,
        this.currentLat,
        lng,
        lat,
      );

      // Si la distancia es >= 100m y tenemos servicio OSRM, usar geometría de ruta
      if (distance >= 100 && this.services.osrm) {
        await this.animateAlongOSRMPath(lng, lat, duration);
      } else {
        // Animación lineal para distancias cortas
        this.animateToPosition(lng, lat, duration);
      }
    } else {
      // Actualización inmediata sin animación
      this.cancelAnimation();
      const prevLng = this.currentLng;
      const prevLat = this.currentLat;
      this.currentLng = lng;
      this.currentLat = lat;
      this.iconMarker?.setLngLat([lng, lat]);
      this.textMarker?.setLngLat([lng, lat]);
      this.lastUpdateTimestamp = Date.now();

      // Emitir trail para movimiento sin animación
      if (
        this.services.onRoutePathUpdate &&
        (prevLng !== lng || prevLat !== lat)
      ) {
        try {
          this.services.onRoutePathUpdate([
            [prevLng, prevLat],
            [lng, lat],
          ]);
        } catch (e) {
          console.warn('Error emitiendo trail sin animación:', e);
        }
      }
    }
  }

  /**
   * Actualiza el estado (velocidad, ignición) y color del anillo si cambió
   */
  public async updateState(
    velocity: number,
    ignition: boolean | undefined,
  ): Promise<void> {
    if (!this.initialized || this.destroyed) return;

    // Detectar cambios significativos que requieren actualizar color
    const cambioIgnicion = this.ignition !== ignition;

    // Cruce del umbral de 5 km/h (stopped <-> moving)
    const cruzoUmbral =
      (this.velocity <= 5 && velocity > 5) ||
      (this.velocity > 5 && velocity <= 5);

    // Cambio significativo en modo degradado (>5 km/h de diferencia)
    const cambioVelocidadSignificativo =
      Math.abs(this.velocity - velocity) >= 5;

    if (cambioIgnicion || cruzoUmbral || cambioVelocidadSignificativo) {
      await this.updateRingColor(velocity, ignition);
    }

    // Actualizar estado
    this.velocity = velocity;
    this.ignition = ignition;
  }

  /**
   * Actualiza configuración del marker sin recrearlo
   */
  public async updateConfig(
    changes: Partial<HTMLVehicleMarkerConfig>,
  ): Promise<void> {
    if (!this.initialized || this.destroyed) return;

    const oldConfig = { ...this.config };
    this.config = { ...this.config, ...changes };

    // Aplicar cambios sin recrear
    if (changes.scale !== undefined && changes.scale !== oldConfig.scale) {
      this.applyScale(changes.scale);
    }

    if (
      changes.showText !== undefined &&
      changes.showText !== oldConfig.showText
    ) {
      if (changes.showText && !this.textMarker) {
        await this.createTextMarker();
      } else if (!changes.showText && this.textMarker) {
        this.textMarker.remove();
        this.textMarker = undefined;
      }
    }

    if (
      changes.pitchAlignment !== undefined &&
      changes.pitchAlignment !== oldConfig.pitchAlignment
    ) {
      // Recrear solo el marker de ícono con nueva alineación
      if (this.iconMarker) {
        const element = this.iconMarker.getElement();
        this.iconMarker.remove();

        this.iconMarker = new mapboxgl.Marker({
          element: element,
          anchor: 'center',
          rotationAlignment: 'viewport',
          pitchAlignment: changes.pitchAlignment,
        })
          .setLngLat([this.currentLng, this.currentLat])
          .addTo(this.map);
      }
    }

    if (changes.animationDuration !== undefined) {
      // Solo actualizar el valor, afectará próximas animaciones
      this.config.animationDuration = changes.animationDuration;
    }
  }

  /**
   * Obtiene la posición actual (incluso durante animación)
   */
  public getPosition(): { lng: number; lat: number } {
    return { lng: this.currentLng, lat: this.currentLat };
  }

  /**
   * Obtiene el heading actual
   */
  public getHeading(): number {
    return this.heading;
  }

  /**
   * Verifica si hay una animación en curso
   */
  public isAnimating(): boolean {
    return this.animation !== undefined;
  }

  /**
   * Destruye el marker y limpia todos los recursos
   */
  public destroy(): void {
    if (this.destroyed) return;

    // Cancelar animación
    this.cancelAnimation();

    // Remover markers de Mapbox
    this.iconMarker?.remove();
    this.textMarker?.remove();
    this.iconMarker = undefined;
    this.textMarker = undefined;

    // Limpiar listeners
    this.cleanupListeners();

    this.destroyed = true;
    this.initialized = false;
  }

  // ========================================
  // MÉTODOS PRIVADOS
  // ========================================

  /**
   * Crea los elementos HTML y markers de Mapbox
   */
  private async createMarkerElements(): Promise<void> {
    // Crear marker de ícono + anillo
    const iconElement = await this.createIconElement();

    this.iconMarker = new mapboxgl.Marker({
      element: iconElement,
      anchor: 'center',
      rotationAlignment: 'viewport',
      pitchAlignment: this.config.pitchAlignment,
    })
      .setLngLat([this.currentLng, this.currentLat])
      .addTo(this.map);

    // Prevenir que el fog atmosférico de Mapbox reduzca la opacity del marker seleccionado
    this.iconMarker.getElement().classList.add('selected-vehicle-marker');

    // Crear marker de texto si es necesario
    if (this.config.showText) {
      await this.createTextMarker();
    }

    // Aplicar rotación inicial
    this.updateRotation();
  }

  /**
   * Crea el elemento HTML del ícono + anillo
   */
  private async createIconElement(): Promise<HTMLElement> {
    const valores = this.services.helper.getValores(this.reporte);
    const velocidad = valores.velocidad || 0;
    const ignicion = valores.ignicion;

    // Obtener estilos personalizados
    const estilosData = await this.services.getVehiculoEstilos(
      this.reporte,
      this.config.categoria,
    );
    const vista = estilosData?.vista;
    const grupoColor = estilosData?.grupoColor;

    // Calcular color del ícono
    let iconColor: string | undefined;
    if (vista?.useOriginalIconColor) {
      iconColor = undefined;
    } else if (grupoColor && vista?.groupColorIcon) {
      iconColor = grupoColor;
    } else {
      iconColor =
        this.config.categoria === 'tipo'
          ? vista?.vehiculeTypeIconColor
          : vista?.vehicleFunctionIconColor;
    }

    if (!iconColor && !vista?.useOriginalIconColor) {
      iconColor = '#5196ff';
    }

    // Calcular color de fondo
    let backgroundColor = '#ffffff';
    if (grupoColor && vista?.groupColorBackground) {
      backgroundColor = grupoColor;
    }

    // Determinar URL del ícono o foto
    const mostrarFoto = this.config.mostrarFoto ?? 'no';
    const photoUrl =
      mostrarFoto !== 'no'
        ? ((this.reporte.activo as any)?.imagenMiniatura || this.reporte.activo?.imagenes?.[0])
        : undefined;

    const iconUrl =
      this.config.categoria === 'tipo'
        ? vista?.vehicleTypeIcon
        : vista?.vehicleFunctionIcon;

    // Generar ícono compuesto (foto o tipo/función)
    const { imageData: compositeImageData } = photoUrl
      ? await this.services.vehicleIconGenerator.generateVehiclePhotoIcon({
          photoUrl,
          backgroundColor,
          isSelected: true,
          modo: mostrarFoto as 'centrado' | 'completo',
        })
      : await this.services.vehicleIconGenerator.generateVehicleIcon({
          iconUrl:
            iconUrl || 'assets/map/vehiculos/Markers Default/Tipo/auto.png',
          backgroundColor,
          iconColor,
          isSelected: true,
        });

    // Calcular color del anillo según estado
    const estadoColor = this.services.vehicleIconGenerator.calculateRingColor(
      velocidad,
      ignicion,
      {
        offColor: vista?.offColor,
        stopColor: vista?.stopColor,
        movingColor: vista?.movingColor,
        movingDegraded: vista?.movingDegraded,
      },
    );

    // Generar anillo de orientación
    const ringImageData =
      this.services.vehicleIconGenerator.generateOrientationRing(
        estadoColor,
        960,
        true,
      );

    // Convertir a data URIs
    const compositeDataUrl =
      this.services.vehicleIconGenerator.imageDataToDataUrl(compositeImageData);
    const ringDataUrl =
      this.services.vehicleIconGenerator.imageDataToDataUrl(ringImageData);

    // Crear estructura HTML
    const markerEl = document.createElement('div');
    markerEl.className = 'vehicle-html-marker';
    markerEl.dataset['vehicleId'] = this.reporte.activo?._id || '';

    const markerInner = document.createElement('div');
    markerInner.className = 'marker-inner';
    markerInner.style.position = 'relative';
    // 20% más grande porque es el seleccionado (1.2x)
    markerInner.style.width = `${66 * this.config.scale * 1.2}px`;
    markerInner.style.height = `${66 * this.config.scale * 1.2}px`;

    // Capa 1: Ícono compuesto (fondo + ícono)
    const background = document.createElement('div');
    background.className = 'marker-background';
    background.style.position = 'absolute';
    background.style.top = '0';
    background.style.left = '0';
    background.style.width = '100%';
    background.style.height = '100%';
    background.style.backgroundImage = `url(${compositeDataUrl})`;
    background.style.backgroundSize = 'contain';
    background.style.backgroundPosition = 'center';
    background.style.backgroundRepeat = 'no-repeat';

    // Capa 2: Anillo de orientación
    const ring = document.createElement('div');
    ring.className = 'marker-orientation-ring';
    ring.style.position = 'absolute';
    ring.style.top = '0';
    ring.style.left = '0';
    ring.style.width = '100%';
    ring.style.height = '100%';
    ring.style.backgroundImage = `url(${ringDataUrl})`;
    ring.style.backgroundSize = 'contain';
    ring.style.backgroundPosition = 'center';
    ring.style.backgroundRepeat = 'no-repeat';
    ring.dataset['color'] = estadoColor;

    markerInner.appendChild(background);
    markerInner.appendChild(ring);
    markerEl.appendChild(markerInner);

    return markerEl;
  }

  /**
   * Crea el marker de texto
   */
  private async createTextMarker(): Promise<void> {
    const textElement = this.createTextElement();

    // 20% más grande porque es el seleccionado (1.2x)
    const finalSize = 66 * this.config.scale * 1.2;
    const offsetY = -(finalSize / 2 + 8);

    this.textMarker = new mapboxgl.Marker({
      element: textElement,
      anchor: 'bottom',
      offset: [0, offsetY],
      rotationAlignment: 'viewport',
      pitchAlignment: 'viewport',
    })
      .setLngLat([this.currentLng, this.currentLat])
      .addTo(this.map);
  }

  /**
   * Crea el elemento HTML del texto
   */
  private createTextElement(): HTMLElement {
    const textEl = document.createElement('div');
    textEl.className = 'vehicle-text-marker';
    textEl.textContent = this.reporte.activo?.identificacion || '';
    textEl.style.fontSize = '12px';
    textEl.style.fontWeight = 'bold';
    textEl.style.color = '#ffffff';
    textEl.style.textShadow =
      '#000000 0px 0px 3px, #000000 0px 0px 3px, #000000 0px 0px 3px, #000000 0px 0px 3px, #000000 0px 0px 3px, #000000 0px 0px 3px';
    textEl.style.whiteSpace = 'nowrap';
    textEl.style.pointerEvents = 'none';
    textEl.dataset['vehicleId'] = this.reporte.activo?._id || '';

    return textEl;
  }

  /**
   * Configura listeners del mapa
   */
  private setupMapListeners(): void {
    // Listener para rotación del mapa
    this.rotateHandler = () => {
      this.updateRotation();
    };

    this.map.on('rotate', this.rotateHandler);
  }

  /**
   * Actualiza la rotación del anillo según el heading y bearing del mapa
   */
  private updateRotation(): void {
    if (!this.iconMarker) return;

    const markerElement = this.iconMarker.getElement();
    const ring = markerElement.querySelector(
      '.marker-orientation-ring',
    ) as HTMLElement;
    if (!ring) return;

    // Ajustar rotación: heading del vehículo - bearing del mapa
    const mapBearing = this.map.getBearing();
    const rotation = this.heading - mapBearing;

    ring.style.transform = `rotate(${rotation}deg)`;
  }

  /**
   * Actualiza el color del anillo
   */
  private async updateRingColor(
    velocity: number,
    ignition: boolean | undefined,
  ): Promise<void> {
    if (!this.iconMarker) return;

    const markerElement = this.iconMarker.getElement();
    const ring = markerElement.querySelector(
      '.marker-orientation-ring',
    ) as HTMLElement;
    if (!ring) return;

    // Obtener estilos personalizados
    const estilosData = await this.services.getVehiculoEstilos(
      this.reporte,
      this.config.categoria,
    );
    const vista = estilosData?.vista;

    // Calcular nuevo color
    const estadoColor = this.services.vehicleIconGenerator.calculateRingColor(
      velocity,
      ignition,
      {
        offColor: vista?.offColor,
        stopColor: vista?.stopColor,
        movingColor: vista?.movingColor,
        movingDegraded: vista?.movingDegraded,
      },
    );

    // Verificar si realmente cambió el color
    const colorActual = ring.dataset['color'];
    if (colorActual === estadoColor) {
      return;
    }

    // Regenerar imagen del anillo
    const ringImageData =
      this.services.vehicleIconGenerator.generateOrientationRing(
        estadoColor,
        960,
        true,
      );
    const ringDataUrl =
      this.services.vehicleIconGenerator.imageDataToDataUrl(ringImageData);

    // Actualizar
    ring.style.backgroundImage = `url(${ringDataUrl})`;
    ring.dataset['color'] = estadoColor;
  }

  /**
   * Calcula la duración dinámica de animación basada en el tiempo transcurrido y distancia
   * @param targetLng Longitud de destino
   * @param targetLat Latitud de destino
   * @returns Duración en milisegundos
   */
  private calculateDynamicDuration(
    targetLng: number,
    targetLat: number,
  ): number {
    const now = Date.now();
    const MIN_DURATION = 1000; // 1 segundo
    const MAX_DURATION = 30000; // 30 segundos
    const MAX_VISUAL_VELOCITY = 50; // m/s (~180 km/h)

    // Si es la primera actualización, usar duración por defecto
    if (this.lastUpdateTimestamp === 0) {
      this.lastUpdateTimestamp = now;
      return this.config.animationDuration;
    }

    // Calcular tiempo transcurrido desde última actualización
    const timeSinceLastUpdate = now - this.lastUpdateTimestamp;
    this.lastUpdateTimestamp = now;

    // Calcular distancia entre posición actual y destino
    const distance = this.calculateDistance(
      this.currentLng,
      this.currentLat,
      targetLng,
      targetLat,
    );

    // Si la distancia es muy pequeña (< 10m), usar duración mínima
    if (distance < 10) {
      return MIN_DURATION;
    }

    // Calcular velocidad requerida (m/s)
    const requiredVelocity = distance / (timeSinceLastUpdate / 1000);

    // Si la velocidad requerida es mayor al límite visual, ajustar duración
    if (requiredVelocity > MAX_VISUAL_VELOCITY) {
      // Alargar la animación para mantener velocidad visual realista
      const adjustedDuration = (distance / MAX_VISUAL_VELOCITY) * 1000;
      return Math.min(adjustedDuration, MAX_DURATION);
    }

    // Usar el tiempo real transcurrido (con límites)
    return Math.max(MIN_DURATION, Math.min(timeSinceLastUpdate, MAX_DURATION));
  }

  /**
   * Calcula la distancia entre dos puntos en metros (fórmula de Haversine)
   * @param lng1 Longitud punto 1
   * @param lat1 Latitud punto 1
   * @param lng2 Longitud punto 2
   * @param lat2 Latitud punto 2
   * @returns Distancia en metros
   */
  private calculateDistance(
    lng1: number,
    lat1: number,
    lng2: number,
    lat2: number,
  ): number {
    const R = 6371e3; // Radio de la Tierra en metros
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δφ = ((lat2 - lat1) * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;

    const a =
      Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) * Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distancia en metros
  }

  /**
   * Calcula el bearing (dirección) entre dos puntos en grados (0-360)
   * @param lng1 Longitud punto 1
   * @param lat1 Latitud punto 1
   * @param lng2 Longitud punto 2
   * @param lat2 Latitud punto 2
   * @returns Bearing en grados
   */
  private calculateBearing(
    lng1: number,
    lat1: number,
    lng2: number,
    lat2: number,
  ): number {
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;

    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x =
      Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    const θ = Math.atan2(y, x);

    return ((θ * 180) / Math.PI + 360) % 360; // Normalizar a 0-360
  }

  /**
   * Anima el marker a lo largo de una ruta obtenida de OSRM
   * @param targetLng Longitud de destino
   * @param targetLat Latitud de destino
   * @param duration Duración de la animación
   */
  private async animateAlongOSRMPath(
    targetLng: number,
    targetLat: number,
    duration: number,
  ): Promise<void> {
    try {
      // Obtener ruta de OSRM
      const routeResponse = await this.services.osrm.getRoute(
        [this.currentLng, this.currentLat],
        [targetLng, targetLat],
      );

      // Si no se obtuvo ruta, hacer animación lineal como fallback
      if (
        !routeResponse ||
        !routeResponse.geometry ||
        routeResponse.geometry.length === 0
      ) {
        console.warn('OSRM no devolvió ruta, usando animación lineal');
        this.animateToPosition(targetLng, targetLat, duration);
        return;
      }

      // Obtener coordenadas del path
      const pathCoordinates: [number, number][] = routeResponse.geometry;

      // Si el path tiene menos de 2 puntos, usar animación lineal
      if (pathCoordinates.length < 2) {
        this.animateToPosition(targetLng, targetLat, duration);
        return;
      }

      // Animar a lo largo del path (el trail se revela progresivamente dentro de animateAlongPath)
      this.animateAlongPath(pathCoordinates, duration);
    } catch (error) {
      console.error('Error obteniendo ruta OSRM:', error);
      // Fallback a animación lineal
      this.animateToPosition(targetLng, targetLat, duration);
    }
  }

  /**
   * Anima el marker a lo largo de un array de coordenadas
   * @param pathCoordinates Array de coordenadas [lng, lat]
   * @param duration Duración total de la animación
   */
  private animateAlongPath(
    pathCoordinates: [number, number][],
    duration: number,
  ): void {
    // Cancelar animación anterior
    this.cancelAnimation();

    // Calcular longitud total del path
    const totalDistance = this.calculatePathLength(pathCoordinates);

    // Configurar nueva animación
    this.animation = {
      frame: 0,
      originLng: this.currentLng,
      originLat: this.currentLat,
      targetLng: pathCoordinates[pathCoordinates.length - 1][0],
      targetLat: pathCoordinates[pathCoordinates.length - 1][1],
      startTime: performance.now(),
      duration,
    };

    // Tracking: índice del último waypoint ya emitido al trail
    let lastEmittedWaypoint = -1;
    let lastEmitTime = 0;
    const EMIT_INTERVAL = 50; // ms entre emisiones para no saturar

    // Iniciar animación
    const animate = (currentTime: number) => {
      if (!this.animation || !this.iconMarker) return;

      const elapsed = currentTime - this.animation.startTime;
      const progress = Math.min(elapsed / this.animation.duration, 1);

      // Calcular posición en el path basado en progreso
      const currentDistance = progress * totalDistance;
      const result = this.getPositionAtDistance(
        pathCoordinates,
        currentDistance,
      );

      if (result) {
        const { lng, lat, bearing, segmentIndex } = result;

        // Actualizar posición actual
        this.currentLng = lng;
        this.currentLat = lat;

        // Actualizar heading basado en el bearing del segmento
        this.heading = bearing;
        this.updateRotation();

        // Actualizar markers
        this.iconMarker.setLngLat([lng, lat]);
        this.textMarker?.setLngLat([lng, lat]);

        // Emitir trail progresivamente cada EMIT_INTERVAL ms
        // Incluye: waypoints pasados + posición interpolada actual del marker
        // segmentIndex = i → marker entre pathCoordinates[i-1] y pathCoordinates[i]
        // waypoints pasados: pathCoordinates[0..i-1]
        if (
          this.services.onRoutePathUpdate &&
          currentTime - lastEmitTime >= EMIT_INTERVAL
        ) {
          const trailCoords: [number, number][] = [];
          const passedUpTo = segmentIndex - 1;

          // Agregar waypoints completamente pasados (no emitidos aún)
          if (passedUpTo > lastEmittedWaypoint) {
            for (let i = lastEmittedWaypoint + 1; i <= passedUpTo; i++) {
              trailCoords.push(pathCoordinates[i]);
            }
            lastEmittedWaypoint = passedUpTo;
          }

          // Agregar posición interpolada actual como punta del trail
          // (está sobre la línea entre pathCoordinates[i-1] y pathCoordinates[i], sin zigzag)
          trailCoords.push([lng, lat]);

          if (trailCoords.length > 0) {
            try {
              this.services.onRoutePathUpdate(trailCoords);
              lastEmitTime = currentTime;
            } catch (e) {
              console.warn('Error emitiendo trail progresivo:', e);
            }
          }
        }
      }

      // Continuar o finalizar
      if (progress < 1) {
        this.animation.frame = this.services.ngZone.runOutsideAngular(() => {
          return requestAnimationFrame(animate);
        });
      } else {
        // Al finalizar, emitir TODOS los waypoints restantes (incluido el destino)
        if (this.services.onRoutePathUpdate) {
          const remaining: [number, number][] = [];
          for (
            let i = lastEmittedWaypoint + 1;
            i < pathCoordinates.length;
            i++
          ) {
            remaining.push(pathCoordinates[i]);
          }
          if (remaining.length > 0) {
            try {
              this.services.onRoutePathUpdate(remaining);
            } catch (e) {
              console.warn('Error emitiendo trail final:', e);
            }
          }
        }
        this.cleanupAnimation();
      }
    };

    this.animation.frame = this.services.ngZone.runOutsideAngular(() => {
      return requestAnimationFrame(animate);
    });
  }

  /**
   * Calcula la longitud total de un path
   * @param pathCoordinates Array de coordenadas
   * @returns Longitud total en metros
   */
  private calculatePathLength(pathCoordinates: [number, number][]): number {
    let totalLength = 0;

    for (let i = 1; i < pathCoordinates.length; i++) {
      const [lng1, lat1] = pathCoordinates[i - 1];
      const [lng2, lat2] = pathCoordinates[i];
      totalLength += this.calculateDistance(lng1, lat1, lng2, lat2);
    }

    return totalLength;
  }

  /**
   * Obtiene la posición y bearing a una distancia dada en el path
   * @param pathCoordinates Array de coordenadas
   * @param targetDistance Distancia objetivo en metros
   * @returns Posición (lng, lat) y bearing, o null si no se puede calcular
   */
  private getPositionAtDistance(
    pathCoordinates: [number, number][],
    targetDistance: number,
  ): {
    lng: number;
    lat: number;
    bearing: number;
    segmentIndex: number;
  } | null {
    if (pathCoordinates.length < 2) return null;

    let accumulatedDistance = 0;

    for (let i = 1; i < pathCoordinates.length; i++) {
      const [lng1, lat1] = pathCoordinates[i - 1];
      const [lng2, lat2] = pathCoordinates[i];
      const segmentLength = this.calculateDistance(lng1, lat1, lng2, lat2);

      if (accumulatedDistance + segmentLength >= targetDistance) {
        // La posición objetivo está en este segmento
        const distanceInSegment = targetDistance - accumulatedDistance;
        const ratio = segmentLength > 0 ? distanceInSegment / segmentLength : 0;

        // Interpolar posición en el segmento
        const lng = this.lerp(lng1, lng2, ratio);
        const lat = this.lerp(lat1, lat2, ratio);

        // Calcular bearing del segmento
        const bearing = this.calculateBearing(lng1, lat1, lng2, lat2);

        return { lng, lat, bearing, segmentIndex: i };
      }

      accumulatedDistance += segmentLength;
    }

    // Si llegamos aquí, estamos al final del path
    const lastPoint = pathCoordinates[pathCoordinates.length - 1];
    const prevPoint = pathCoordinates[pathCoordinates.length - 2];
    const bearing = this.calculateBearing(
      prevPoint[0],
      prevPoint[1],
      lastPoint[0],
      lastPoint[1],
    );

    return {
      lng: lastPoint[0],
      lat: lastPoint[1],
      bearing,
      segmentIndex: pathCoordinates.length - 1,
    };
  }

  /**
   * Anima el marker hacia una nueva posición
   */
  private animateToPosition(
    targetLng: number,
    targetLat: number,
    duration: number,
  ): void {
    // Cancelar animación anterior
    this.cancelAnimation();

    const originLng = this.currentLng;
    const originLat = this.currentLat;

    // Configurar nueva animación
    this.animation = {
      frame: 0,
      originLng,
      originLat,
      targetLng,
      targetLat,
      startTime: performance.now(),
      duration,
    };

    // Tracking para emitir trail progresivamente
    let lastEmittedLng = originLng;
    let lastEmittedLat = originLat;
    // Emitir cada ~50ms para no saturar actualizaciones del layer
    let lastEmitTime = 0;
    const EMIT_INTERVAL = 50;

    // Iniciar animación
    const animate = (currentTime: number) => {
      if (!this.animation || !this.iconMarker) return;

      const elapsed = currentTime - this.animation.startTime;
      const progress = Math.min(elapsed / this.animation.duration, 1);

      // Interpolar posición (lineal, sin easing)
      const newLng = this.lerp(
        this.animation.originLng,
        this.animation.targetLng,
        progress,
      );
      const newLat = this.lerp(
        this.animation.originLat,
        this.animation.targetLat,
        progress,
      );

      // Actualizar posición actual
      this.currentLng = newLng;
      this.currentLat = newLat;

      // Actualizar markers
      this.iconMarker.setLngLat([newLng, newLat]);
      this.textMarker?.setLngLat([newLng, newLat]);

      // Emitir trail progresivamente durante la animación
      if (
        this.services.onRoutePathUpdate &&
        currentTime - lastEmitTime >= EMIT_INTERVAL
      ) {
        try {
          this.services.onRoutePathUpdate([
            [lastEmittedLng, lastEmittedLat],
            [newLng, newLat],
          ]);
          lastEmittedLng = newLng;
          lastEmittedLat = newLat;
          lastEmitTime = currentTime;
        } catch (e) {
          console.warn('Error emitiendo trail en animación lineal:', e);
        }
      }

      // Continuar o finalizar
      if (progress < 1) {
        this.animation.frame = this.services.ngZone.runOutsideAngular(() => {
          return requestAnimationFrame(animate);
        });
      } else {
        // Emitir último segmento si quedó pendiente
        if (
          this.services.onRoutePathUpdate &&
          (lastEmittedLng !== targetLng || lastEmittedLat !== targetLat)
        ) {
          try {
            this.services.onRoutePathUpdate([
              [lastEmittedLng, lastEmittedLat],
              [targetLng, targetLat],
            ]);
          } catch (e) {
            console.warn('Error emitiendo trail final lineal:', e);
          }
        }
        this.cleanupAnimation();
      }
    };

    this.animation.frame = this.services.ngZone.runOutsideAngular(() => {
      return requestAnimationFrame(animate);
    });
  }

  /**
   * Cancela la animación en curso
   */
  private cancelAnimation(): void {
    if (this.animation) {
      if (this.animation.frame) {
        cancelAnimationFrame(this.animation.frame);
      }
      this.cleanupAnimation();
    }
  }

  /**
   * Limpia el estado de animación y listeners temporales
   */
  private cleanupAnimation(): void {
    // Limpiar listeners de interacción
    this.interactionHandlers.forEach(({ event, handler }) => {
      this.map.off(event, handler);
    });
    this.interactionHandlers = [];

    this.animation = undefined;
  }

  /**
   * Aplica escala al marker sin recrearlo
   */
  private applyScale(scale: number): void {
    if (!this.iconMarker) return;

    const markerElement = this.iconMarker.getElement();
    const markerInner = markerElement.querySelector(
      '.marker-inner',
    ) as HTMLElement;

    if (markerInner) {
      // 20% más grande porque es el seleccionado (1.2x)
      const newSize = 66 * scale * 1.2;
      markerInner.style.width = `${newSize}px`;
      markerInner.style.height = `${newSize}px`;
    }

    // Actualizar offset del texto si existe
    if (this.textMarker) {
      const textElement = this.textMarker.getElement();
      // 20% más grande porque es el seleccionado (1.2x)
      const finalSize = 66 * scale * 1.2;
      const offsetY = -(finalSize / 2 + 8);

      // El tamaño del texto NO se modifica, siempre es 12px fijo

      // Recrear el marker de texto con nuevo offset
      const currentPos = this.textMarker.getLngLat();
      this.textMarker.remove();

      this.textMarker = new mapboxgl.Marker({
        element: textElement,
        anchor: 'bottom',
        offset: [0, offsetY],
        rotationAlignment: 'viewport',
        pitchAlignment: 'viewport',
      })
        .setLngLat(currentPos)
        .addTo(this.map);
    }
  }

  /**
   * Limpia todos los listeners
   */
  private cleanupListeners(): void {
    if (this.rotateHandler) {
      this.map.off('rotate', this.rotateHandler);
      this.rotateHandler = undefined;
    }

    this.cleanupAnimation();
  }

  /**
   * Interpolación lineal
   */
  private lerp(start: number, end: number, t: number): number {
    return (1 - t) * start + t * end;
  }
}
